import { EligibleRegisterGroup, GroupType } from '@schooly/api';

export type RegisterGroupsFilters = {
  ageGroupIds: string[];
  subjectIds: string[];
  onlyTutorGroups: boolean;
};

export const emptyRegisterGroupsFilters = (): RegisterGroupsFilters => ({
  ageGroupIds: [],
  subjectIds: [],
  onlyTutorGroups: false,
});

export const hasActiveFilters = (filters: RegisterGroupsFilters): boolean =>
  !!filters.ageGroupIds.length || !!filters.subjectIds.length || filters.onlyTutorGroups;

export const filterRegisterGroups = <T extends EligibleRegisterGroup>(
  groups: T[],
  query: string,
  filters: RegisterGroupsFilters,
): T[] => {
  const normalizedQuery = query.trim().toLowerCase();

  return groups.filter((group) => {
    const matchesName = !normalizedQuery || group.name.toLowerCase().includes(normalizedQuery);
    const matchesAgeGroup =
      !filters.ageGroupIds.length ||
      group.age_group_entries.some((entry) => filters.ageGroupIds.includes(entry.age_group.id));
    const matchesTutor = !filters.onlyTutorGroups || group.group_type === GroupType.TutorGroup;
    const matchesSubject =
      !filters.subjectIds.length ||
      (!!group.subject && filters.subjectIds.includes(group.subject.id));

    return matchesName && matchesAgeGroup && matchesTutor && matchesSubject;
  });
};

export type RegisterGroupsFilterOptions = {
  showTutorOnly: boolean;
  availableSubjectIds: string[];
};

export const getRegisterGroupsFilterOptions = (
  sidebarGroups: EligibleRegisterGroup[],
): RegisterGroupsFilterOptions => {
  const subjectIds = new Set<string>();
  let showTutorOnly = false;

  for (const group of sidebarGroups) {
    if (group.subject?.id) subjectIds.add(group.subject.id);
    if (group.group_type === GroupType.TutorGroup) showTutorOnly = true;
  }

  return { showTutorOnly, availableSubjectIds: [...subjectIds] };
};

export const sanitizeRegisterGroupsFilters = (
  filters: RegisterGroupsFilters,
  options: RegisterGroupsFilterOptions,
): RegisterGroupsFilters => ({
  ageGroupIds: filters.ageGroupIds,
  subjectIds: filters.subjectIds.filter((id) => options.availableSubjectIds.includes(id)),
  onlyTutorGroups: filters.onlyTutorGroups && options.showTutorOnly,
});
